import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound, MessageCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { PageHeader, Panel, DarkBtn, Field } from "@/components/admin/AdminUI";

const GROUPS = [
  ["Ana Sayfa", [["hero_title", "Hero başlık"], ["hero_subtitle", "Hero alt metin", true], ["tagline", "Slogan"], ["about_title", "Hikaye başlığı"], ["about_text", "Hikaye metni", true]]],
  ["İletişim", [["name", "Restoran adı"], ["phone", "Telefon"], ["whatsapp", "WhatsApp (905xx… formatında)"], ["email", "E-posta"], ["address", "Adres"], ["hours", "Çalışma saatleri"], ["instagram", "Instagram kullanıcı adı"], ["map_query", "Harita arama metni"]]],
  ["SEO (Google)", [["seo_title", "Google başlığı (en fazla ~60 karakter)"], ["seo_description", "Google açıklaması (en fazla ~155 karakter)", true]]],
  ["Menü & Duyuru", [["announcement", "Üst duyuru bandı"], ["menu_note", "Menü alt notu (KDV / alerjen bilgisi)", true]]],
  ["Yasal Künye (6563 s. Kanun gereği)", [["company_title", "Ticari unvan"], ["company_tax", "Vergi dairesi / no"], ["company_mersis", "MERSİS no"], ["kep_address", "KEP adresi"]]],
];

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/auth/password", f);
      toast.success("Şifre güncellendi");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Panel className="p-6">
      <h2 className="font-serif-ym text-2xl font-semibold flex items-center gap-2"><KeyRound size={18} /> Şifre Değiştir</h2>
      <form onSubmit={submit} className="mt-4 grid sm:grid-cols-3 gap-3 items-end" data-testid="password-form">
        <Field label="Mevcut şifre"><Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="password-current-input" /></Field>
        <Field label="Yeni şifre (min 6)"><Input type="password" required minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="password-new-input" /></Field>
        <DarkBtn type="submit" data-testid="password-save-btn">Güncelle</DarkBtn>
      </form>
    </Panel>
  );
}

function NotifyCard({ s, setS }) {
  const [configured, setConfigured] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/admin/notify/status").then(({ data }) => setConfigured(data.whatsapp_configured));
  }, []);
  const test = async () => {
    setSending(true);
    try {
      await api.put("/admin/settings", s);
      const { data } = await api.post("/admin/notify/test");
      if (data.ok) toast.success("Test mesajı gönderildi, WhatsApp'ınızı kontrol edin.");
      else toast.error(data.results?.[0]?.error || "Mesaj gönderilemedi");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <Panel className="p-6" data-testid="notify-card">
      <h2 className="font-serif-ym text-2xl font-semibold flex items-center gap-2"><MessageCircle size={18} /> WhatsApp Rezervasyon Bildirimi</h2>
      <p className="text-sm text-[#6B5E56] mt-1">Siteden her yeni rezervasyon geldiğinde aşağıdaki numaralara anında WhatsApp mesajı gider.</p>
      <p className={`mt-3 inline-block rounded-full px-3 py-1 text-xs ${configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="notify-status-badge">
        {configured === null ? "Kontrol ediliyor…" : configured ? "WhatsApp bağlantısı hazır" : "WhatsApp bağlantı bilgileri henüz girilmedi"}
      </p>
      <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-4 items-end">
        <Field label="Bildirim numaraları" hint="Birden fazla numarayı virgülle ayırın. Örn: 905559612464, 905321112233">
          <Input value={s.notify_whatsapp || ""} onChange={(e) => setS({ ...s, notify_whatsapp: e.target.value })} data-testid="settings-notify_whatsapp-input" />
        </Field>
        <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={s.notify_whatsapp_active !== false} onCheckedChange={(v) => setS({ ...s, notify_whatsapp_active: v })} data-testid="settings-notify-active-switch" /> Aktif</label>
        <DarkBtn onClick={test} disabled={sending} data-testid="notify-test-btn"><Send size={15} /> {sending ? "Gönderiliyor…" : "Test gönder"}</DarkBtn>
      </div>
    </Panel>
  );
}

export default function Settings() {
  const { reload } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      reload();
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;
  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Site Ayarları" desc="Sitedeki tüm metin ve iletişim bilgileri.">
        <DarkBtn onClick={save} disabled={saving} data-testid="admin-settings-save-btn"><Save size={16} /> {saving ? "Kaydediliyor…" : "Kaydet"}</DarkBtn>
      </PageHeader>
      <div className="space-y-6">
        <Panel className="p-6 flex flex-wrap gap-8">
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!s.announcement_active} onCheckedChange={(v) => setS({ ...s, announcement_active: v })} data-testid="settings-announcement-switch" /> Duyuru bandı aktif</label>
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!s.reservations_open} onCheckedChange={(v) => setS({ ...s, reservations_open: v })} data-testid="settings-reservations-switch" /> Online rezervasyon açık</label>
        </Panel>
        <NotifyCard s={s} setS={setS} />
        {GROUPS.map(([title, fields]) => (
          <Panel key={title} className="p-6">
            <h2 className="font-serif-ym text-2xl font-semibold">{title}</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {fields.map(([k, label, long]) => (
                <div key={k} className={long ? "sm:col-span-2" : ""}>
                  <Field label={label}>
                    {long ? (
                      <Textarea rows={3} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
                    ) : (
                      <Input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
                    )}
                  </Field>
                </div>
              ))}
            </div>
          </Panel>
        ))}
        <PasswordCard />
      </div>
    </div>
  );
}
